import { useEffect, useRef, useState } from "react";
import {
  Link2,
  LockKeyhole,
  MessageSquareText,
  RefreshCw,
  Search,
  Send,
  UsersRound,
} from "lucide-react";
import { cliqAction, getCollaborationStatus, startCliqOAuth } from "./lib/api";
import type { CollaborationStatus } from "./lib/collaborationContract";
import type { ConflictArchiveMessage } from "./lib/collaborationContract";
import "./team-chat.css";

export function TeamChat({
  open = true,
  onClose,
  onUnreadChange,
}: {
  open?: boolean;
  onClose?: () => void;
  onUnreadChange?: (unread:boolean) => void;
}) {
  const windowRef=useRef<HTMLDivElement>(null),dragRef=useRef<{pointerId:number;offsetX:number;offsetY:number}|null>(null),resizeRef=useRef<{pointerId:number;startX:number;startY:number;width:number;height:number}|null>(null);
  const [position,setPosition]=useState(()=>({x:Math.max(12,window.innerWidth-600),y:76}));
  const [size,setSize]=useState({width:560,height:620});
  const [status, setStatus] = useState<CollaborationStatus | null>(null),
    [error, setError] = useState(""),
    [connecting, setConnecting] = useState(false),
    [chats, setChats] = useState<
      Array<{ id: string; title?: string; name?: string;unread_count?:number;unreadCount?:number }>
    >([]),
    [conversationSearch,setConversationSearch]=useState(""),
    [conversationListOpen,setConversationListOpen]=useState(true),
    [selected, setSelected] = useState(""),
    [messages, setMessages] = useState<
      Array<{ id: string; text?: string; message?: string }>
    >([]),
    [draft, setDraft] = useState(""),
    [archive,setArchive]=useState<ConflictArchiveMessage[]>([]),
    [archiveOpen,setArchiveOpen]=useState(false);
  useEffect(()=>{const clamp=()=>setPosition(current=>{const rect=windowRef.current?.getBoundingClientRect();return{x:Math.max(8,Math.min(current.x,window.innerWidth-(rect?.width||560)-8)),y:Math.max(8,Math.min(current.y,window.innerHeight-(rect?.height||620)-8))}});window.addEventListener("resize",clamp);return()=>window.removeEventListener("resize",clamp)},[]);
  const startDrag=(event:React.PointerEvent<HTMLDivElement>)=>{if(window.innerWidth<=720||(event.target as HTMLElement).closest("button"))return;const rect=windowRef.current?.getBoundingClientRect();if(!rect)return;dragRef.current={pointerId:event.pointerId,offsetX:event.clientX-rect.left,offsetY:event.clientY-rect.top};event.currentTarget.setPointerCapture(event.pointerId)};
  const moveDrag=(event:React.PointerEvent<HTMLDivElement>)=>{const drag=dragRef.current,rect=windowRef.current?.getBoundingClientRect();if(!drag||drag.pointerId!==event.pointerId||!rect)return;setPosition({x:Math.max(8,Math.min(event.clientX-drag.offsetX,window.innerWidth-rect.width-8)),y:Math.max(8,Math.min(event.clientY-drag.offsetY,window.innerHeight-rect.height-8))})};
  const endDrag=(event:React.PointerEvent<HTMLDivElement>)=>{if(dragRef.current?.pointerId===event.pointerId)dragRef.current=null};
  const resizeBy=(amount:number)=>setSize(current=>({width:Math.max(420,Math.min(current.width+amount,window.innerWidth-position.x-8)),height:Math.max(420,Math.min(current.height+amount,window.innerHeight-position.y-8))}));
  const startResize=(event:React.PointerEvent<HTMLButtonElement>)=>{if(window.innerWidth<=720)return;const rect=windowRef.current?.getBoundingClientRect();if(!rect)return;resizeRef.current={pointerId:event.pointerId,startX:event.clientX,startY:event.clientY,width:rect.width,height:rect.height};const move=(pointer:PointerEvent)=>{const resize=resizeRef.current;if(!resize||resize.pointerId!==pointer.pointerId)return;setSize({width:Math.max(420,Math.min(resize.width+pointer.clientX-resize.startX,window.innerWidth-position.x-8)),height:Math.max(420,Math.min(resize.height+pointer.clientY-resize.startY,window.innerHeight-position.y-8))})};const stop=(pointer:PointerEvent)=>{if(resizeRef.current?.pointerId!==pointer.pointerId)return;resizeRef.current=null;window.removeEventListener("pointermove",move);window.removeEventListener("pointerup",stop);window.removeEventListener("pointercancel",stop)};window.addEventListener("pointermove",move);window.addEventListener("pointerup",stop);window.addEventListener("pointercancel",stop);event.preventDefault()};
  const load = () => {
    setError("");
    getCollaborationStatus()
      .then(setStatus)
      .catch(() =>
        setError(
          "Connection status is unavailable. No provider action was attempted.",
        ),
      );
  };
  useEffect(load, []);
  const connect = async () => {
    setConnecting(true);
    setError("");
    try {
      window.location.assign(await startCliqOAuth());
    } catch {
      setError("The team chat connection could not be started.");
    } finally {
      setConnecting(false);
    }
  };
  const connected = status?.configured && status.connection === "connected";
  useEffect(() => {
    if(!connected){onUnreadChange?.(false);return}
    let active=true;
    const refreshChats=()=>cliqAction<|{data?:Array<{id:string;title?:string;name?:string;unread_count?:number;unreadCount?:number}>}|Array<{id:string;title?:string;name?:string;unread_count?:number;unreadCount?:number}>>("list_chats").then(value=>{if(!active)return;const records=Array.isArray(value)?value:value.data||[];setChats(records);onUnreadChange?.(records.some(chat=>Number(chat.unread_count??chat.unreadCount??0)>0))}).catch(()=>{if(active)setError("Conversations could not be loaded.")});
    refreshChats();const timer=window.setInterval(refreshChats,30000);return()=>{active=false;window.clearInterval(timer)};
  }, [connected,onUnreadChange]);
  const openChat = async (id: string) => {
    setSelected(id);
    const remaining=chats.map(chat=>chat.id===id?{...chat,unread_count:0,unreadCount:0}:chat);setChats(remaining);onUnreadChange?.(remaining.some(chat=>Number(chat.unread_count??chat.unreadCount??0)>0));
    setError("");
    try {
      const value = await cliqAction<
        | { data?: Array<{ id: string; text?: string; message?: string }> }
        | Array<{ id: string; text?: string; message?: string }>
      >("list_messages", { conversationId: id });
      setMessages(Array.isArray(value) ? value : value.data || []);
    } catch {
      setError("Messages could not be loaded.");
    }
  };
  const send = async () => {
    if (!selected || !draft.trim()) return;
    try {
      await cliqAction("send_message", {
        conversationId: selected,
        text: draft.trim(),
      });
      setDraft("");
      await openChat(selected);
    } catch {
      setError("The message was not accepted. No delivery is claimed.");
    }
  };
  if (!open) return null;
  return (
    <div ref={windowRef} className="persistent-chat panel" aria-label="Team chat" style={{left:position.x,top:position.y,width:size.width,height:size.height}}>
      <div className="persistent-chat-heading" onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
        <div className="chat-window-title"><MessageSquareText size={17}/><strong>Team chat</strong><span className={`integration-status ${connected?"connected":"needs-config"}`}>{connected?"connected":"disconnected"}</span></div>
        <div className="chat-window-actions">
          <div className="chat-size-controls" aria-label="Team chat window size"><button className="icon-btn" aria-label="Make team chat smaller" onClick={()=>resizeBy(-80)}>−</button><button className="icon-btn" aria-label="Make team chat larger" onClick={()=>resizeBy(80)}>+</button></div>
          <button className="icon-btn" aria-label="Refresh team chat" onClick={load}><RefreshCw size={15}/></button>
          {onClose && (
            <button
              className="icon-btn"
              aria-label="Close team chat"
              onClick={onClose}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <section className="chat-development-notice" aria-label="Team chat development status">
        <strong>In development</strong>
        <span>Team Chat is visible for evaluation, but availability and behavior may change while development continues.</span>
      </section>
      {!connected&&<section className="chat-connect-strip">
        <span>{status?.configured?"Connect your secure chat identity to continue.":"Chat connection is not configured."}</span>
        <div>{!connected && (
          <button
            className="primary-btn"
            disabled={!status?.configured || connecting}
            onClick={connect}
          >
            <Link2 size={15} />
            {connecting ? "Opening connection…" : "Connect my chat account"}
          </button>
        )}</div>
      </section>}
      {status?.conflictArchiveAccess&&<section className="panel collaboration-status"><div><strong>Conflict resolution archive</strong><span>CRM-captured messages only · automatic deletion after 14 days</span></div><button className="secondary-btn" onClick={async()=>{if(archiveOpen){setArchiveOpen(false);return}try{setArchive(await cliqAction<ConflictArchiveMessage[]>("list_conflict_archive"));setArchiveOpen(true)}catch{setError("Conflict resolution archive access was rejected.")}}}>{archiveOpen?"Close archive":"Review archive"}</button></section>}
      {archiveOpen&&<section className="panel"><h3>Conflict resolution archive</h3>{archive.length?archive.map(item=><div className="email-message inbound" key={item.id}><strong>{item.sender_label||"Chat user"}</strong><p>{item.message_body}</p><small>{new Date(item.occurred_at).toLocaleString()} · expires {new Date(item.expires_at).toLocaleDateString()}</small></div>):<div className="empty-state">No retained CRM-captured messages.</div>}</section>}
      {error && (
        <div className="runtime-banner degraded">
          <LockKeyhole size={16} />
          <span>{error}</span>
        </div>
      )}
      <div className={`collaboration-shell persistent ${conversationListOpen?"":"list-collapsed"}`}>
        <aside className={`panel collaboration-list ${conversationListOpen?"":"collapsed"}`}>
          <button className="conversation-list-toggle" aria-label={conversationListOpen?"Collapse conversations":"Expand conversations"} onClick={()=>setConversationListOpen(value=>!value)}><MessageSquareText size={16}/>{chats.some(chat=>Number(chat.unread_count??chat.unreadCount??0)>0)&&<span className="unread-dot" aria-label="New messages"/>}<span>{conversationListOpen?"Conversations":""}</span><b>{conversationListOpen?"‹":"›"}</b></button>
          {conversationListOpen&&<>
          <div className="collaboration-search">
            <Search size={15} />
            <input disabled={!connected} placeholder="Search conversations" value={conversationSearch} onChange={event=>setConversationSearch(event.target.value)}/>
          </div>
          {connected && chats.length ? (
            chats.filter(chat=>(chat.title||chat.name||"").toLowerCase().includes(conversationSearch.toLowerCase())).map((chat) => (
              <button
                className={`calendar-agenda-row ${selected === chat.id ? "active" : ""}`}
                key={chat.id}
                onClick={() => openChat(chat.id)}
              >
                <MessageSquareText size={16} />
                <strong>
                  {chat.title || chat.name || "Conversation"}
                </strong>
                {Number(chat.unread_count??chat.unreadCount??0)>0&&<span className="unread-dot" aria-label="New messages"/>}
              </button>
            ))
          ) : connected ? (
            <div className="empty-state">
              No conversations are available.
            </div>
          ) : (
            <div className="empty-state">
              <LockKeyhole size={24} />
              <strong>Delegated connection required</strong>
              <span>
                Connect your own chat identity to browse permitted conversations.
              </span>
            </div>
          )}</>}
        </aside>
        <section className="panel collaboration-thread">
          <div className="panel-header">
            <h3>Conversation</h3>
            <span
              className={`integration-status ${selected ? "connected" : "disabled"}`}
            >
              {selected ? "selected" : "no selection"}
            </span>
          </div>
          {selected ? (
            <div className="message-thread">
              {messages.map((message) => (
                <div className="email-message inbound" key={message.id}>
                  <p>
                    {message.text ||
                      message.message ||
                      "Unsupported message content"}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <UsersRound size={28} />
              <strong>No conversation selected</strong>
              <span>
                Select a conversation to keep chatting without leaving your
                current CRM work.
              </span>
            </div>
          )}
          <div className="collaboration-composer">
            <textarea
              disabled={!selected}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write an internal message…"
            />
            <button
              className="primary-btn"
              disabled={!selected || !draft.trim()}
              onClick={send}
            >
              <Send size={15} />
              Send
            </button>
          </div>
        </section>
      </div>
      <button className="chat-resize-handle" aria-label="Resize team chat" onPointerDown={startResize}/>
    </div>
  );
}
